/**
 * Border U.S. customs PDF modal form.
 * @see docs/OPT_IN_ORDER_DOCUMENTS.md
 */

import { OutputTemplateDocumentType } from "@prisma/client";

import { CountrySelect } from "~/components/_shared/inputs/CountrySelect";
import { DocumentAttributePrefillField } from "~/components/domain/settings/documents/DocumentAttributePrefillField";
import { DocumentDisplayUomModalOverrideField } from "~/components/domain/settings/documents/DocumentDisplayUomModalOverrideField";
import { Form } from "~/components/Form";
import {
  type BorderUsDocumentInput,
  BorderUsDocumentInputSchema,
  borderUsDocumentFormInitialValues,
} from "~/modules/orders/order-documents/border-us/schema";
import { DocumentTypedSignatureModalField } from "~/modules/orders/order-documents/DocumentTypedSignatureModalField";
import { FieldWithPrefillHint } from "~/modules/orders/order-documents/FieldWithPrefillHint";
import { borderUsPlannedPrefillHints } from "~/modules/orders/order-documents/order-document-sfdc-field-bindings";
import { PrintSourceRadio } from "~/modules/orders/order-documents/PrintSourceRadio";
import { useBolDocumentAddressLabels } from "~/modules/orders/order-documents/useBolDocumentAddressLabels";
import { DocumentAddressOverrideEditor } from "~/modules/orders/order-menu-document-extensions/DocumentAddressOverrideEditor";
import { OptInOrderDocumentStringDateInput } from "~/modules/orders/order-menu-document-extensions/OptInOrderDocumentStringDateInput";
import { OrderDocumentSavedFacts } from "~/modules/orders/order-menu-document-extensions/OrderDocumentSavedFacts";
import type { OrderDocumentFormProps } from "~/modules/orders/order-menu-document-extensions/types";
import { useOptInOrderDocumentForm } from "~/modules/orders/order-menu-document-extensions/useOptInOrderDocumentForm";
import { OPT_IN_ORDER_DOCUMENT_SUBMIT_LABEL, ORDER_DOCUMENT_SLUG } from "~/shared/documents/order-document-settings";
import { DOCUMENT_ATTRIBUTE_WIRE_KEYS } from "~/shared/documents/schemas/document-attribute-wiring.schema";
import { Divider, Group, Stack, Text, Textarea, TextInput } from "~/ui";
import { api } from "~/utils/api";

export const BorderUsDocumentForm = ({ orderId, sourceType = "order", onSuccess, onCancel }: OrderDocumentFormProps) => {
  const modalSettingsQuery = api.org.getOrderDocumentDisplayUomForCreateModal.useQuery({ slug: ORDER_DOCUMENT_SLUG.borderUs });
  const { borderUsConsigneeFieldLabel } = useBolDocumentAddressLabels();
  const {
    form,
    prefillQuery,
    mutation,
    handleSubmit,
    prefillFeedback,
    isPrefillReady,
    attributePrefillCandidates,
    addressCandidates,
    savedFacts,
    useOutputTemplate,
    setUseOutputTemplate,
  } = useOptInOrderDocumentForm<BorderUsDocumentInput>({
    orderId,
    sourceType,
    slug: ORDER_DOCUMENT_SLUG.borderUs,
    schema: BorderUsDocumentInputSchema,
    onSuccess,
    initialValues: { ...borderUsDocumentFormInitialValues(), orderId, sourceType },
  });
  const wireKeys = DOCUMENT_ATTRIBUTE_WIRE_KEYS.borderUs;

  return (
    <Form
      form={form}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      submitText={OPT_IN_ORDER_DOCUMENT_SUBMIT_LABEL}
      loading={prefillQuery.isFetching || mutation.isPending}
      feedback={prefillFeedback}
      mutationError={mutation.error}
      disabled={!isPrefillReady}
    >
      <Stack spacing="sm">
        {sourceType === "order" && (
          <PrintSourceRadio
            documentType={OutputTemplateDocumentType.BORDER_US}
            value={useOutputTemplate}
            onChange={setUseOutputTemplate}
            legacyOptionsNote
          />
        )}
        {!useOutputTemplate && (
          <>
            <OrderDocumentSavedFacts
              facts={[
                { label: "Approximate crossing", value: form.values.approximateCrossingDate },
                { label: "Border crossing point", value: form.values.exportBorderCrossing },
                { label: "Port of entry", value: form.values.importCrossingLocation },
                { label: "Freight method", value: savedFacts?.freightMethod ?? form.values.containerType },
              ]}
            />
            <Divider label="Cover letter" />
            <FieldWithPrefillHint hint={borderUsPlannedPrefillHints.operatingEntity}>
              <DocumentAddressOverrideEditor
                label="Cover letter — operating entity contact"
                value={form.values.addressOverrides?.operatingEntity}
                onChange={value => form.setFieldValue("addressOverrides.operatingEntity", value)}
                error={form.errors.operatingEntity ?? form.errors["addressOverrides.operatingEntity"]}
              />
            </FieldWithPrefillHint>
            <Divider label="Pro forma — parties" />
            <FieldWithPrefillHint hint={borderUsPlannedPrefillHints.exporter}>
              <DocumentAddressOverrideEditor
                label="Exporter or Seller"
                withAsterisk={sourceType === "purchaseOrder"}
                sourceLabel="Exporter address source"
                candidates={addressCandidates}
                value={form.values.addressOverrides?.exporter}
                onChange={value => form.setFieldValue("addressOverrides.exporter", value)}
                error={form.errors.exporter ?? form.errors["addressOverrides.exporter"]}
              />
            </FieldWithPrefillHint>
            <FieldWithPrefillHint hint={borderUsPlannedPrefillHints.consignee}>
              <DocumentAddressOverrideEditor
                label={borderUsConsigneeFieldLabel}
                withAsterisk={sourceType === "purchaseOrder"}
                sourceLabel="Consignee address source"
                candidates={addressCandidates}
                preferredCandidateId="delivery"
                value={form.values.addressOverrides?.consignee}
                onChange={value => form.setFieldValue("addressOverrides.consignee", value)}
                error={form.errors.consignee ?? form.errors["addressOverrides.consignee"]}
              />
            </FieldWithPrefillHint>
            <FieldWithPrefillHint hint={borderUsPlannedPrefillHints.buyer}>
              <DocumentAddressOverrideEditor
                label="Buyer (Sold To)"
                value={form.values.addressOverrides?.buyer}
                onChange={value => form.setFieldValue("addressOverrides.buyer", value)}
                error={form.errors.buyer ?? form.errors["addressOverrides.buyer"]}
              />
            </FieldWithPrefillHint>
            <FieldWithPrefillHint hint={borderUsPlannedPrefillHints.buyerFedId}>
              <TextInput label="Buyer Fed Tax Number" {...form.getInputProps("buyerFedId")} />
            </FieldWithPrefillHint>
            <FieldWithPrefillHint hint={borderUsPlannedPrefillHints.importerOfRecord}>
              <DocumentAddressOverrideEditor
                label="Importer of Record"
                withAsterisk={sourceType === "purchaseOrder"}
                value={form.values.addressOverrides?.importerOfRecord}
                onChange={value => form.setFieldValue("addressOverrides.importerOfRecord", value)}
                error={form.errors.importerOfRecord ?? form.errors["addressOverrides.importerOfRecord"]}
              />
            </FieldWithPrefillHint>
            <Divider label="Pro forma — shipment" />
            <Group grow align="flex-start">
              <OptInOrderDocumentStringDateInput
                label="Invoice date"
                description="Date on this pro forma invoice; defaults to today."
                value={form.values.invoiceDate ?? ""}
                onChange={value => form.setFieldValue("invoiceDate", value)}
                error={form.errors.invoiceDate}
              />
              <TextInput label="Date of sale" description="Uses the saved order date." value={form.values.dateOfSale ?? ""} readOnly />
            </Group>
            <FieldWithPrefillHint hint={borderUsPlannedPrefillHints.shipperConsigneeRef}>
              <TextInput label="Shipper/consignee ref#" {...form.getInputProps("shipperConsigneeRef")} />
            </FieldWithPrefillHint>
            <FieldWithPrefillHint hint={borderUsPlannedPrefillHints.destinationCountry}>
              <TextInput label="Destination (Country)" {...form.getInputProps("destinationCountry")} />
            </FieldWithPrefillHint>
            <FieldWithPrefillHint hint={borderUsPlannedPrefillHints.countryOfOrigin}>
              <CountrySelect label="Country of origin" clearable={false} {...form.getInputProps("countryOfOrigin")} />
            </FieldWithPrefillHint>
            <Divider label="Line items" />
            {form.values.lineItems.map((line, index) => (
              <Stack key={line.orderItemId} spacing="xs">
                <Text size="sm" weight={600}>
                  Line {index + 1}
                </Text>
                <FieldWithPrefillHint hint={borderUsPlannedPrefillHints.lineDescription}>
                  <Textarea label="Description of goods" readOnly value={line.description} minRows={2} />
                </FieldWithPrefillHint>
                <Group grow align="flex-start">
                  <FieldWithPrefillHint hint={borderUsPlannedPrefillHints.lineCountryOfManufacture}>
                    <DocumentAttributePrefillField
                      label="Country of mfg"
                      candidates={attributePrefillCandidates.lines[line.orderItemId]?.[wireKeys.lineCountryOfManufacture]}
                      value={form.values.lineItems[index]?.countryOfManufacture ?? ""}
                      onSelect={value => form.setFieldValue(`lineItems.${index}.countryOfManufacture`, value)}
                    >
                      <TextInput label="Country of mfg" {...form.getInputProps(`lineItems.${index}.countryOfManufacture`)} />
                    </DocumentAttributePrefillField>
                  </FieldWithPrefillHint>
                  <FieldWithPrefillHint hint={borderUsPlannedPrefillHints.lineHsNumber}>
                    <DocumentAttributePrefillField
                      label="H.S. number"
                      candidates={attributePrefillCandidates.lines[line.orderItemId]?.[wireKeys.lineHsNumber]}
                      value={form.values.lineItems[index]?.hsNumber ?? ""}
                      onSelect={value => form.setFieldValue(`lineItems.${index}.hsNumber`, value)}
                    >
                      <TextInput label="H.S. number" {...form.getInputProps(`lineItems.${index}.hsNumber`)} />
                    </DocumentAttributePrefillField>
                  </FieldWithPrefillHint>
                  <FieldWithPrefillHint hint={borderUsPlannedPrefillHints.lineQuantityDisplay}>
                    <TextInput label="Qty/Packaging" {...form.getInputProps(`lineItems.${index}.quantityDisplay`)} />
                  </FieldWithPrefillHint>
                </Group>
              </Stack>
            ))}
            <Divider label="Pro forma — footer" />
            <FieldWithPrefillHint hint={borderUsPlannedPrefillHints.authorizedBy}>
              <TextInput label="Authorized by (name, organization)" {...form.getInputProps("authorizedBy")} />
            </FieldWithPrefillHint>
            <Divider label="TSCA certification" />
            <Text size="sm">The TSCA certificate is always included with “Comply with TSCA” checked.</Text>
            <DocumentTypedSignatureModalField
              required
              description="Enter the signer's name. Prints as the signature on the pro forma and TSCA certificate."
              value={form.values.typedSignature}
              onChange={typedSignature => form.setFieldValue("typedSignature", typedSignature)}
              error={typeof form.errors.typedSignature === "string" ? form.errors.typedSignature : undefined}
            />
            {modalSettingsQuery.data?.allowDisplayUomOverrideOnCreate && (
              <>
                <DocumentDisplayUomModalOverrideField
                  orgSettings={modalSettingsQuery.data}
                  value={form.values.displayUomOverride}
                  onChange={displayUomOverride => form.setFieldValue("displayUomOverride", displayUomOverride)}
                />
                <Text size="sm" color="dimmed">
                  Applies to gross weight. Qty/Packaging keeps the units entered on each line.
                </Text>
              </>
            )}
          </>
        )}
      </Stack>
    </Form>
  );
};
